import React, { useMemo } from "react";
import Stepper from "../Stepper";
import { Button, Typography } from "@thingsw/pitta-design-system";
import { useTranslation } from "react-i18next";
import { makeStyles, Theme } from "@material-ui/core";
import ArrowBack from "@material-ui/icons/ArrowBack";
import { Skeleton } from "@material-ui/lab";
import useRole from "../../hooks/useRole";
import { LightColors } from "@thingsw/pitta-modules";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: "absolute",
    zIndex: 1000,
    top: 71,
    right: 180,
    width: 343,
    padding: 20,
    display: "flex",
    flexDirection: "column",
    gap: 16,
    alignItems: "start",
    backgroundColor: LightColors.primary["0"],
    borderRadius: 12,
    border: `2px solid ${LightColors.primary["6"]}`,
  },
}));

interface FleetaOnboardingStepProps {
  currentStep: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
}

const FleetaOnboardingStep2 = ({
  currentStep,
  onNext,
  onPrev,
  onSkip,
}: FleetaOnboardingStepProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [isVideoLoaded, setIsVideoLoaded] = React.useState(false);
  const { isDriver } = useRole();

  const stepCount = useMemo(() => {
    return isDriver ? 3 : 6;
  }, [isDriver]);

  return (
    <>
      <div
        style={{
          position: "absolute",
          top: 50,
          right: 250,
          zIndex: 1001,
        }}
      >
        <svg
          width="31"
          height="32"
          viewBox="0 0 31 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M16.2771 2.14512C15.8949 1.46517 14.9159 1.46517 14.5337 2.14512L3.25264 22.2126C2.87791 22.8792 3.35964 23.7026 4.12435 23.7026L26.6864 23.7026C27.4512 23.7026 27.9329 22.8792 27.5581 22.2126L16.2771 2.14512Z"
            fill="white"
          />
          <path
            d="M27.4054 22.5945L15.4054 1.59448"
            stroke="#E9E9EA"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M3.4054 22.5945L15.4054 1.59448"
            stroke="#E9E9EA"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </div>
      <div className={classes.root}>
        <video
          onLoadedData={() => setIsVideoLoaded(true)}
          style={{
            width: 300,
            height: "100%",
            objectFit: "cover",
            boxSizing: "border-box",
            borderRadius: 12,
            display: isVideoLoaded ? "block" : "none",
          }}
          autoPlay
          muted
          loop
          playsInline
          webkit-playsinline
        >
          <source src="/video/onboarding2.mp4" type="video/mp4" />
        </video>
        {!isVideoLoaded && (
          <Skeleton
            variant="rect"
            style={{
              width: 300,
              height: 300,
              borderRadius: 12,
            }}
          ></Skeleton>
        )}
        <Stepper currentStep={currentStep} stepCount={stepCount} />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <Typography variant="H6">{t("Toggle to switch_")}</Typography>
          <Typography variant="Body">{t("Check the current_")}</Typography>
          <Typography variant="Body">{t("In List View_")}</Typography>
        </div>
        <div
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <div>
            <Button
              variant="text"
              onClick={onPrev}
              color="gray"
              startIcon={<ArrowBack />}
            >
              {t("Back")}
            </Button>
          </div>
          <div
            style={{
              display: "flex",
              gap: 16,
            }}
          >
            <Button onClick={onSkip} variant="text" color="gray">
              {t("Skip tour")}
            </Button>
            <Button
              onClick={onNext}
              variant="contained"
              color="primary"
              style={{
                padding: "8px 0",
                minWidth: 58,
              }}
            >
              {t("Next")}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FleetaOnboardingStep2;
